import { useEffect, useMemo, useState } from "react";
import { X, Plus, Loader2, Radio } from "lucide-react";
import api from "@/lib/api";
import { unwrapCompaniesPage } from "@/lib/companies";
import { toast } from "sonner";
import { canAssignPartners, useAuth } from "@/lib/auth";

const SOURCES = [
  { value: "manual", label: "Manual" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "website", label: "Website" },
  { value: "news", label: "News" },
  { value: "tender", label: "Tender" },
  { value: "referral", label: "Referral" },
  { value: "ask_niq", label: "Ask Niq" },
  { value: "email", label: "Email" },
];

const SIGNAL_TYPES = [
  { value: "manual_entry", label: "Manual entry" },
  { value: "leadership_change", label: "Leadership change" },
  { value: "capex", label: "Capex announcement" },
  { value: "esg_report", label: "ESG report" },
  { value: "tender", label: "Tender / RFP" },
  { value: "demo_request", label: "Demo request" },
  { value: "prospecting", label: "Prospecting" },
];

const inputCls = "h-10 w-full bg-[#F8FCFE] border border-[#D7E8F0] rounded-[10px] px-3 text-[13px] text-[#243B53] outline-none focus:border-[#07539A] focus:bg-white transition-colors";
const fieldCls = "rounded-[16px] bg-white border border-[#C9DCE6] p-3 shadow-[0_4px_14px_rgba(7,83,154,0.04)]";
const labelCls = "text-[10px] uppercase tracking-widest text-[#526D7A] font-semibold";

/**
 * CreateSignalModal — direct signal creation from the Signals screen (B-09).
 * Partner attribution is captured at creation time, not only inherited.
 */
export function CreateSignalModal({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const canAssign = canAssignPartners(user);
  const userPartnerId = user?.partner_id || "";

  const [companies, setCompanies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    company_id: "",
    intent: "",
    source: "manual",
    signal_type: "manual_entry",
    strength: 60,
    ai_summary: "",
    next_action: "",
    partner_id: "",
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      company_id: "",
      intent: "",
      source: "manual",
      signal_type: "manual_entry",
      strength: 60,
      ai_summary: "",
      next_action: "",
      partner_id: userPartnerId || "",
    });
    setLoading(true);
    Promise.all([
      api.get("/companies", { params: { limit: 200 } }),
      api.get("/partner-firms"),
    ])
      .then(([cRes, pRes]) => {
        setCompanies(unwrapCompaniesPage(cRes.data).items);
        setPartners(Array.isArray(pRes.data) ? pRes.data : []);
      })
      .catch(() => toast.error("Could not load form data"))
      .finally(() => setLoading(false));
  }, [open, userPartnerId]);

  const companyOptions = useMemo(
    () => companies.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [companies],
  );

  const partnerOptions = useMemo(
    () => partners.slice().sort((a, b) => (a.firm || a.name || "").localeCompare(b.firm || b.name || "")),
    [partners],
  );

  const userPartnerName = useMemo(() => {
    if (!userPartnerId) return "";
    const p = partners.find((x) => x.id === userPartnerId);
    return p ? (p.firm || p.name) : "Your partner firm";
  }, [partners, userPartnerId]);

  async function submit() {
    if (!form.company_id) {
      toast.error("Please select a company");
      return;
    }
    if (!form.intent.trim()) {
      toast.error("Intent / event description is required");
      return;
    }
    if (canAssign && !form.partner_id) {
      toast.error("Partner attribution is required");
      return;
    }

    const body = {
      company_id: form.company_id,
      intent: form.intent.trim(),
      source: form.source,
      signal_type: form.signal_type,
      strength: Number(form.strength),
      ai_summary: form.ai_summary.trim() || undefined,
      next_action: form.next_action.trim() || undefined,
    };
    if (form.partner_id) body.partner_id = form.partner_id;
    else if (userPartnerId) body.partner_id = userPartnerId;

    setSubmitting(true);
    try {
      const { data } = await api.post("/signals", body);
      toast.success("Signal created");
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create signal");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#12324A]/45 backdrop-blur-sm flex items-start justify-center pt-12 px-4"
      onClick={() => onOpenChange(false)}
      data-testid="create-signal-modal"
    >
      <div
        className="w-full max-w-2xl bg-[#F3FAFD] border border-[#BBD8E7] rounded-[26px] shadow-[0_24px_70px_rgba(7,83,154,0.22)] overflow-hidden flex flex-col max-h-[88vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[#C9DCE6] bg-[#E7F5FB] flex items-center gap-3">
          <div className="w-9 h-9 rounded-[12px] bg-white border border-[#C9DCE6] flex items-center justify-center text-[#07539A]">
            <Radio size={15} />
          </div>
          <div className="font-display text-[18px] text-[#12324A] flex-1">New Signal</div>
          <button onClick={() => onOpenChange(false)} data-testid="create-signal-close" className="w-9 h-9 rounded-full bg-white border border-[#C9DCE6] flex items-center justify-center text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A] transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="py-12 flex flex-col items-center justify-center gap-3 text-[#607D8B] text-[12px]">
              <Loader2 size={18} className="animate-spin" />
              Loading signal fields…
            </div>
          ) : (
            <>
              <div className={fieldCls}>
                <label className={labelCls}>Company *</label>
                <select
                  value={form.company_id}
                  onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                  data-testid="create-signal-company"
                  className={`${inputCls} mt-1.5`}
                >
                  <option value="">Select company…</option>
                  {companyOptions.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className={fieldCls}>
                <label className={labelCls}>Intent / event *</label>
                <input
                  placeholder="e.g. New sustainability head appointed; evaluating ESG platforms"
                  value={form.intent}
                  onChange={(e) => setForm({ ...form, intent: e.target.value })}
                  data-testid="create-signal-intent"
                  className={`${inputCls} mt-1.5`}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className={fieldCls}>
                  <label className={labelCls}>Source</label>
                  <select
                    value={form.source}
                    onChange={(e) => setForm({ ...form, source: e.target.value })}
                    data-testid="create-signal-source"
                    className={`${inputCls} mt-1.5`}
                  >
                    {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div className={fieldCls}>
                  <label className={labelCls}>Signal type</label>
                  <select
                    value={form.signal_type}
                    onChange={(e) => setForm({ ...form, signal_type: e.target.value })}
                    data-testid="create-signal-type"
                    className={`${inputCls} mt-1.5`}
                  >
                    {SIGNAL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              <div className={fieldCls}>
                <div className="flex items-center justify-between gap-3">
                  <label className={labelCls}>Signal strength</label>
                  <span className="min-w-9 h-7 px-2 rounded-full bg-[#E7F5FB] text-[#07539A] text-[11px] font-semibold flex items-center justify-center">{form.strength}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={form.strength}
                  onChange={(e) => setForm({ ...form, strength: Number(e.target.value) })}
                  data-testid="create-signal-strength"
                  className="w-full mt-3 accent-[#07539A]"
                />
              </div>

              <div className={fieldCls}>
                <label className={labelCls}>
                  Partner attribution {canAssign ? "*" : ""}
                </label>
                {userPartnerId && !canAssign ? (
                  <div
                    className="mt-1.5 h-10 flex items-center px-3 rounded-[10px] border border-[#D7E8F0] bg-[#F8FCFE] text-[13px] text-[#07539A]"
                    data-testid="create-signal-partner-readonly"
                  >
                    {userPartnerName}
                  </div>
                ) : (
                  <select
                    value={form.partner_id}
                    onChange={(e) => setForm({ ...form, partner_id: e.target.value })}
                    data-testid="create-signal-partner"
                    className={`${inputCls} mt-1.5`}
                  >
                    <option value="">{canAssign ? "Select partner…" : "Optional — inherits from company"}</option>
                    {partnerOptions.map((p) => (
                      <option key={p.id} value={p.id}>{p.firm || p.name}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className={fieldCls}>
                <label className={labelCls}>Summary (optional)</label>
                <textarea
                  rows={2}
                  placeholder="Context or AI-style summary"
                  value={form.ai_summary}
                  onChange={(e) => setForm({ ...form, ai_summary: e.target.value })}
                  data-testid="create-signal-summary"
                  className="mt-1.5 w-full bg-[#F8FCFE] border border-[#D7E8F0] rounded-[10px] px-3 py-2.5 text-[13px] text-[#243B53] resize-none outline-none focus:border-[#07539A] focus:bg-white transition-colors"
                />
              </div>

              <div className={fieldCls}>
                <label className={labelCls}>Next action (optional)</label>
                <input
                  placeholder="e.g. Reach out to sustainability head"
                  value={form.next_action}
                  onChange={(e) => setForm({ ...form, next_action: e.target.value })}
                  data-testid="create-signal-next-action"
                  className={`${inputCls} mt-1.5`}
                />
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[#C9DCE6] bg-[#E7F5FB] flex justify-end gap-3">
          <button
            onClick={() => onOpenChange(false)}
            className="h-10 px-4 rounded-[12px] text-[12px] border border-[#BBD3DF] bg-white text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || loading}
            data-testid="create-signal-submit"
            className="h-10 px-5 rounded-[12px] text-[12px] font-medium bg-[#07539A] hover:bg-[#063F73] !text-white disabled:opacity-50 flex items-center gap-2 shadow-[0_7px_18px_rgba(7,83,154,0.16)] transition-colors"
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Create signal
          </button>
        </div>
      </div>
    </div>
  );
}
